import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, Gift, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { useSiteSettings } from '../context/SiteSettingsContext';
import { getAttribution, trackFormStart, trackLead } from '../lib/analytics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ExitIntentPopup = () => {
  const { settings } = useSiteSettings();
  const [isVisible, setIsVisible] = useState(false);
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [hasShown, setHasShown] = useState(false);

  // Get settings with defaults
  const enabled = settings.exitPopupEnabled !== false;
  const title = settings.exitPopupTitle || "Wait! Don't Miss Out";
  const subtitle = settings.exitPopupSubtitle || "Get exclusive deals sent to your inbox";
  const benefits = settings.exitPopupBenefits || [
    "First access to new inventory",
    "Exclusive financing offers",
    "Special seasonal promotions"
  ];
  const buttonText = settings.exitPopupButtonText || "Get My Exclusive Deals";
  const dismissText = settings.exitPopupDismissText || "No thanks, I'll pay full price";

  useEffect(() => {
    if (!enabled) return;
    
    const alreadyShown = sessionStorage.getItem('exitPopupShown');
    if (alreadyShown) {
      setHasShown(true);
      return;
    }

    // Only trigger on exit intent - mouse leaving through top of viewport
    const handleMouseLeave = (e) => {
      // Only trigger when mouse leaves through the TOP of the page (exit intent)
      // e.clientY <= 0 means mouse is at/above the top edge
      // Also check that mouse is moving upward (toward browser controls)
      if (e.clientY <= 0 && !hasShown && !sessionStorage.getItem('exitPopupShown')) {
        setIsVisible(true);
        setHasShown(true);
        sessionStorage.setItem('exitPopupShown', 'true');
      }
    };

    // Add a small delay before enabling exit intent detection
    // This prevents accidental triggers on page load
    const enableTimer = setTimeout(() => {
      document.addEventListener('mouseleave', handleMouseLeave);
    }, 3000); // Wait 3 seconds before enabling exit detection
    
    return () => {
      document.removeEventListener('mouseleave', handleMouseLeave);
      clearTimeout(enableTimer);
    };
  }, [hasShown, enabled]);

  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setError('');
    try {
      // Route the captured email into the normal leads pipeline so it actually reaches the team.
      await axios.post(`${API}/inquiries`, {
        name: 'Website Visitor',
        email,
        phone: '',
        subject: 'Exclusive Deals Signup (Exit Popup)',
        message: 'Visitor requested exclusive deals via the exit-intent popup.',
        attribution: getAttribution(),
      });
      trackLead('exit_popup');
      setSubmitted(true);
      setTimeout(() => setIsVisible(false), 3000);
    } catch (err) {
      console.error('Exit popup signup failed:', err);
      setError("Something went wrong. Please try again.");
    }
  };

  if (!isVisible || !enabled) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-scale-in">
        <button
          onClick={() => setIsVisible(false)}
          className="absolute top-4 right-4 w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center z-10"
        >
          <X size={18} className="text-gray-600" />
        </button>

        <div className="bg-gradient-to-r from-red-600 to-red-700 px-6 py-8 text-center">
          <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Gift size={32} className="text-white" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">{title}</h2>
          <p className="text-red-100">{subtitle}</p>
        </div>

        <div className="p-6">
          {!submitted ? (
            <>
              <div className="text-center mb-6">
                <p className="text-gray-700 font-medium mb-2">Subscribe and receive:</p>
                <ul className="text-gray-600 text-sm space-y-1">
                  {benefits.map((benefit, i) => (
                    <li key={i}>✓ {benefit}</li>
                  ))}
                </ul>
              </div>

              <form onSubmit={handleSubmit} onFocus={() => trackFormStart('exit_popup')} className="space-y-4">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email address"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none"
                  required
                />
                <Button 
                  type="submit"
                  className="w-full bg-red-600 hover:bg-red-700 text-white py-3 rounded-lg font-semibold flex items-center justify-center gap-2"
                >
                  {buttonText}
                  <ArrowRight size={18} />
                </Button>
              </form>

              {error && <p className="text-center text-red-600 text-sm mt-3">{error}</p>}
              <p className="text-center text-gray-400 text-xs mt-4">No spam, unsubscribe anytime</p>
            </>
          ) : (
            <div className="text-center py-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">You're In!</h3>
              <p className="text-gray-600">Check your inbox for exclusive offers</p>
            </div>
          )}
        </div>

        <div className="bg-gray-50 px-6 py-3 text-center">
          <button onClick={() => setIsVisible(false)} className="text-gray-500 text-sm hover:text-gray-700">
            {dismissText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExitIntentPopup;
